import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Checkbox from '@/shared/components/input/Checkbox';
import intlShape from '@/shared/utils/intlShape';
import { StateService } from '@/shared/services/StateService';
import api from '@/shared/api';
import config from '@/config';
import messages from './trainingMessages';
import FormattedListValue from '@/shared/components/FormattedListValue';
import DocumentsDropdown from '@/shared/components/DocumentsDropdown';
import {
  IconPencil,
  PersonsIcon,
  DeleteIcon,
  CircleCheckIcon,
  UserAddIcon,
  UndoIcon,
  CancelIcon,
  LinkIcon,
} from '@/shared/components/Icons';
import TrainingCertificateMissingIcon from './TrainingCertificateMissingIcon';
import TrainingEndDateIcon from './TrainingEndDateIcon';

const SUBTABLE_COLSPAN = 12;

const TrainingOverviewRow = ({
  training,
  intl,
  stateService,
  handleSelect,
  onEditTrainingClick,
  onUpdateTrainingEmployeesClick,
  onDeleteTrainingClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [employees, setEmployees] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savingEmployeeId, setSavingEmployeeId] = useState(null);

  const loadEmployees = () => {
    setIsLoading(true);
    api.base
      .get(`${config.appUrls.api}/api/TrainingEmployee/trainingemployees/${training.id}`, {
        params: { pageNumber: 1, pageSize: 1000 },
      })
      .then(({ data }) => {
        setEmployees(data.data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const toggleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && employees === null) {
      loadEmployees();
    }
  };

  const handleRegister = employeeId => {
    setSavingEmployeeId(employeeId);
    api.base
      .post(`${config.appUrls.api}/api/TrainingEmployee/post`, {
        trainingId: training.id,
        employeeId,
      })
      .then(() => loadEmployees())
      .finally(() => setSavingEmployeeId(null));
  };

  const handleChangeStatus = (employeeId, status) => {
    setSavingEmployeeId(employeeId);
    api.base
      .put(`${config.appUrls.api}/api/TrainingEmployee/changestatus`, {
        trainingId: training.id,
        employeeId,
        status,
      })
      .then(() => loadEmployees())
      .finally(() => setSavingEmployeeId(null));
  };

  return (
    <tbody className="hover" key={training.Id} id={`RowID${training.Id}`}>
      <tr>
        <td className="text-nowrap">
          <Checkbox onClick={() => handleSelect(training)} value={training.selected} />
          <a onClick={toggleExpand} className="caret-button">
            <i
              className={classNames('fa pointer', {
                'fa-caret-up': expanded,
                'fa-caret-down': !expanded,
              })}
            />
          </a>
        </td>
        <td>
          <a
            title={intl.formatMessage(messages.EditTraining)}
            onClick={() => {
              onEditTrainingClick(training);
            }}
          >
            <b>{training.name}</b>
          </a>
        </td>
        <td>{training.description}</td>
        <td>{training.resource.name}</td>
        <td>
          <FormattedListValue list="trainingStatus" id={training.status} />
        </td>
        <td>
          <FormattedListValue list="trainingType" id={training.type} />
        </td>
        <td>{training.durationHours}</td>
        <td>{intl.formatDate(training.startDate)}</td>
        <td>{intl.formatDate(training.endDate)}</td>
        <td>
          <i
            className={`fa ${training.mandatory ? 'fa-check text-success' : 'fa-times text-muted'}`}
          />
        </td>
        <td>
          <i
            className={`fa ${
              training.certificateRequired ? 'fa-check text-success' : 'fa-times text-muted'
            }`}
          />
        </td>
        <td>
          {training.completedTrainingEmployeeCount}/{training.totalTrainingEmployeeCount}
        </td>
        <td className="text-right">
          <a
            className="btn btn-xs btn-icon margin5R"
            title={intl.formatMessage(messages.EditTraining)}
            onClick={() => {
              onEditTrainingClick(training);
            }}
          >
            <IconPencil />
          </a>
          <a
            className="btn btn-xs btn-icon margin5R"
            title={intl.formatMessage(messages.AddUsers)}
            onClick={() => {
              onUpdateTrainingEmployeesClick(training, loadEmployees);
            }}
          >
            <PersonsIcon />
          </a>
          <a
            className="btn btn-xs btn-icon margin5R"
            title={intl.formatMessage(messages.OpenRegistrationOverview)}
            onClick={() =>
              stateService.gotoRegistrationOverview({
                trainingId: training.id,
                period: 'all',
              })
            }
          >
            <LinkIcon />
          </a>
          <a
            className="btn btn-xs btn-icon"
            title={intl.formatMessage(messages.Delete)}
            onClick={() => {
              onDeleteTrainingClick(training);
            }}
          >
            <DeleteIcon />
          </a>
        </td>
      </tr>
      {expanded && (
        <tr className="subclasses">
          <td />
          <td colSpan={SUBTABLE_COLSPAN}>
            <div className="subtable-wrapper width100pct">
              {isLoading ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (
                <table className="table subtable">
                  <caption>{intl.formatMessage(messages.Trainings)}</caption>
                  <thead>
                    <tr>
                      <th>{intl.formatMessage(messages.Name)}</th>
                      <th>{intl.formatMessage(messages.Status)}</th>
                      <th>{intl.formatMessage(messages.RegisteredTrainingHours)}</th>
                      <th>{intl.formatMessage(messages.CompletedDate)}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(employees || [])
                      .filter(emp => emp.status != null)
                      .map(emp => (
                        <tr key={emp.employeeId}>
                          <td>
                            {emp.employeeFirstName} {emp.employeeLastName}
                          </td>
                          <td>
                            {emp.status != null && (
                              <FormattedListValue list="trainingStatus" id={emp.status} />
                            )}
                            {emp.status === 2 && emp.certificateMissing && (
                              <span className="margin5L">
                                <TrainingCertificateMissingIcon id={emp.id} />
                              </span>
                            )}
                            {emp.status != null && emp.status !== 2 && emp.status !== 3 && (
                              <span className="margin5L">
                                <TrainingEndDateIcon id={emp.id} endDate={training.endDate} />
                              </span>
                            )}
                          </td>
                          <td>
                            {emp.trainingEmployeeDurationMinutes
                              ? emp.trainingEmployeeDurationMinutes.minuteToTime()
                              : ''}
                          </td>
                          <td>{emp.completedDate ? intl.formatDate(emp.completedDate) : ''}</td>
                          <td className="text-right">
                            <a
                              className="btn btn-xs btn-icon margin5R"
                              title={intl.formatMessage(messages.OpenRegistrationOverview)}
                              onClick={() =>
                                stateService.gotoRegistrationOverview({
                                  userId: emp.userId,
                                  trainingId: training.id,
                                  period: 'all',
                                })
                              }
                            >
                              <LinkIcon />
                            </a>
                            {emp.status === 3 && (
                              <a
                                className="btn btn-xs btn-icon"
                                title={intl.formatMessage(messages.RegisterEmployee)}
                                disabled={savingEmployeeId === emp.employeeId}
                                onClick={() => handleRegister(emp.employeeId)}
                              >
                                <UserAddIcon />
                              </a>
                            )}
                            {emp.status === 2 && (
                              <a
                                className="btn btn-xs btn-icon"
                                title={intl.formatMessage(messages.ReopenTrainingEmployee)}
                                disabled={savingEmployeeId === emp.employeeId}
                                onClick={() => handleChangeStatus(emp.employeeId, 1)}
                              >
                                <UndoIcon />
                              </a>
                            )}
                            {emp.status != null && emp.status !== 2 && emp.status !== 3 && (
                              <a
                                className="btn btn-xs btn-icon"
                                title={intl.formatMessage(messages.MarkAsComplete)}
                                disabled={savingEmployeeId === emp.employeeId}
                                onClick={() => handleChangeStatus(emp.employeeId, 2)}
                              >
                                <CircleCheckIcon />
                              </a>
                            )}
                            {emp.status != null && emp.status !== 2 && emp.status !== 3 && (
                              <a
                                className="btn btn-xs btn-icon"
                                title={intl.formatMessage(messages.CancelEmployee)}
                                disabled={savingEmployeeId === emp.employeeId}
                                onClick={() => handleChangeStatus(emp.employeeId, 3)}
                              >
                                <CancelIcon />
                              </a>
                            )}
                            <DocumentsDropdown
                              className="btn-icon margin5R"
                              linkId={emp.id}
                              linkType={13}
                              documentType={100000}
                              informQueues={false}
                              readOnly
                              downloadOnClick
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </tbody>
  );
};

TrainingOverviewRow.propTypes = {
  intl: intlShape.isRequired,
  training: PropTypes.object.isRequired,
  handleSelect: PropTypes.func.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  onEditTrainingClick: PropTypes.func.isRequired,
  onUpdateTrainingEmployeesClick: PropTypes.func.isRequired,
  onDeleteTrainingClick: PropTypes.func.isRequired,
};

export default TrainingOverviewRow;
